import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { PaginationBar, paginate } from "@/components/Pagination";
import { CategoryCombo, BrandCombo, LinkBrandToggle, linkBrand } from "@/components/SkuFields";
import TechSpecEditor from "@/components/TechSpecEditor";
import ActivityLog from "@/components/ActivityLog";
import { useAuth } from "@/context/AuthContext";
import { Package, Plus, Search, Trash2, Loader2, FileText, Upload, Download, Boxes, Tag, X } from "lucide-react";

const UOMS = ["EA", "PCS", "SET", "BOX", "M", "M²", "KG", "L", "ROLL", "PAIR", "PKT", "UNIT"];
const fmtSize = (b) => (!b ? "—" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const empty = { name: "", internal_description: "", category: "", uom: "EA", brand: "", mpn: "", default_supplier_id: "", notes: "",
  is_stocked: false, origin: "", landed_cost_usd: "", default_margin_pct: "", margin_basis: "", on_order: "", confirmed: "", hs_code: "", duty_pct: "" };

function ProductDialog({ open, onOpenChange, initial, suppliers, categories, onSaved }) {
  const { user } = useAuth();
  const isOwner = (user?.email || "").toLowerCase() === "ronick@somcoregroup.com";
  const editing = !!initial?.id;
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) {
      setF(initial ? { ...empty, ...initial, default_supplier_id: initial.default_supplier_id || "", hs_code: initial.hs_code || "", duty_pct: initial.duty_rate == null ? "" : String(Math.round(initial.duty_rate * 10000) / 100) } : empty);
      setSheets(initial?.datasheets || []);
    }
  }, [open, initial]);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Client description is required"); return; }
    setBusy(true);
    try {
      const body = { name: f.name, internal_description: f.internal_description || "", category: f.category, uom: f.uom, brand: f.brand, mpn: f.mpn,
        default_supplier_id: f.default_supplier_id === "none" ? "" : f.default_supplier_id, notes: f.notes,
        is_stocked: !!f.is_stocked, origin: f.origin || "",
        landed_cost_usd: Number(f.landed_cost_usd) || 0,
        default_margin_pct: f.default_margin_pct === "" || f.default_margin_pct === null ? null : Number(f.default_margin_pct),
        margin_basis: f.margin_basis || "",
        on_order: Number(f.on_order) || 0, confirmed: Number(f.confirmed) || 0,
        hs_code: (f.hs_code || "").trim(), duty_rate: f.duty_pct === "" || f.duty_pct == null ? null : Number(f.duty_pct) / 100 };
      const { data } = editing ? await api.put(`/catalog/products/${initial.id}`, body) : await api.post(`/catalog/products`, body);
      if (f.link_brand !== false && body.brand && body.default_supplier_id) await linkBrand(body.default_supplier_id, body.brand).catch(() => {});
      toast.success(editing ? "Product updated" : `Created ${data.sku}`);
      onSaved(data);
      if (!editing) onOpenChange(false);
      else setF((p) => ({ ...p, ...data }));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!editing) { toast.error("Save the product first, then add datasheets"); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const { data } = await api.post(`/catalog/products/${initial.id}/datasheets`, { filename: file.name, content_type: file.type || "application/pdf", file_base64: b64 });
      setSheets((s) => [...s, data]);
      toast.success("Datasheet added");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const downloadSheet = async (ds) => {
    try {
      const res = await api.get(`/catalog/products/${initial.id}/datasheets/${ds.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = ds.filename || "datasheet.pdf";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Download failed"); }
  };

  const removeSheet = async (ds) => {
    try { await api.delete(`/catalog/products/${initial.id}/datasheets/${ds.id}`); setSheets((s) => s.filter((x) => x.id !== ds.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="product-dialog">
        <DialogHeader><DialogTitle>{editing ? `${initial.sku} · Edit Product` : "New Product"}</DialogTitle></DialogHeader>
        <div className="grid max-h-[65vh] grid-cols-1 gap-4 overflow-y-auto pr-1 sm:grid-cols-2">
          <div className="grid gap-3 rounded-lg border border-border bg-slate-50/60 p-3 sm:col-span-2 sm:grid-cols-2" data-testid="product-descriptions">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#1B4383]">Client Description *</Label>
              <Textarea value={f.name} onChange={(e) => set("name", e.target.value)} rows={3} placeholder="How the client describes it, e.g. Hydraulic hose 1/2 in, 350 bar" data-testid="product-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#A87F31]">Our / Supplier's Description</Label>
              <Textarea value={f.internal_description || ""} onChange={(e) => set("internal_description", e.target.value)} rows={3} placeholder="How we / the supplier describe it" data-testid="product-internal-description" />
            </div>
          </div>
          <div className="grid gap-3 rounded-lg border border-[#D7DEEA] bg-[#F5F8FC] p-3 sm:col-span-2 sm:grid-cols-2" data-testid="product-customs">
            <div className="text-xs font-semibold text-[#1B4383] sm:col-span-2">Customs <span className="font-normal text-muted-foreground">— fills in automatically on future RFQ costings for this SKU</span></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-600">HS Code</Label><Input value={f.hs_code || ""} onChange={(e) => set("hs_code", e.target.value)} placeholder="e.g. 8484.10.00" data-testid="product-hs-code" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-600">Duty Rate %</Label><Input inputMode="decimal" value={f.duty_pct ?? ""} onChange={(e) => set("duty_pct", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 10" data-testid="product-duty-rate" /></div>
          </div>
          <div className="grid gap-3 rounded-lg border border-[#BFD6C6] bg-[#F3F8F4] p-3 sm:col-span-2 sm:grid-cols-3" data-testid="product-stock-pricing">
            <div className="text-xs font-semibold text-[#3F6B50] sm:col-span-3">Stock &amp; Pricing <span className="font-normal text-muted-foreground">— used when an RFQ line is quoted from our stock</span></div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Landed Cost / Unit (USD){!isOwner && <span className="ml-1 text-[10px] text-muted-foreground">— only Ronick can edit</span>}</Label>
              <Input inputMode="decimal" disabled={!isOwner} value={f.landed_cost_usd ?? ""} onChange={(e) => set("landed_cost_usd", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" data-testid="product-landed-cost" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Default Margin %</Label>
              <Input inputMode="decimal" value={f.default_margin_pct ?? ""} onChange={(e) => set("default_margin_pct", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Company default" data-testid="product-default-margin" />
            </div>
            <label className="flex items-center gap-2 self-end pb-2 text-xs text-slate-700">
              <input type="checkbox" checked={!!f.is_stocked} onChange={(e) => set("is_stocked", e.target.checked)} data-testid="product-is-stocked" />We keep this in stock
            </label>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Category</Label>
            <CategoryCombo value={f.category} onChange={(v) => set("category", v)} testid="product-category" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Unit of Measure</Label>
            <Select value={f.uom || "EA"} onValueChange={(v) => set("uom", v)}>
              <SelectTrigger data-testid="product-uom"><SelectValue /></SelectTrigger>
              <SelectContent>{UOMS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Brand / OEM</Label>
            <BrandCombo value={f.brand} onChange={(v) => set("brand", v)} testid="product-brand" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Manufacturer Part No.</Label>
            <Input value={f.mpn} onChange={(e) => set("mpn", e.target.value)} placeholder="MPN / part number" data-testid="product-mpn" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-slate-600">Default Supplier</Label>
            <Select value={f.default_supplier_id || "none"} onValueChange={(v) => set("default_supplier_id", v)}>
              <SelectTrigger data-testid="product-supplier"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><LinkBrandToggle brand={f.brand} supplier={suppliers.find((s) => s.id === f.default_supplier_id)} checked={f.link_brand !== false} onChange={(v) => set("link_brand", v)} testid="product-link-brand" /></div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-slate-600">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Internal notes / specs" data-testid="product-notes" />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label className="text-xs text-slate-600">Product Photo & Technical Specification</Label>
            <TechSpecEditor productId={editing ? initial.id : null} />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5 text-xs text-slate-600"><FileText className="h-3.5 w-3.5" />Datasheets (PDF)</Label>
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 aria-disabled:pointer-events-none aria-disabled:opacity-50" aria-disabled={!editing || uploading} data-testid="datasheet-upload">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Add PDF
                <input type="file" accept="application/pdf" className="hidden" onChange={onUpload} disabled={!editing || uploading} />
              </label>
            </div>
            {!editing && <p className="text-xs text-muted-foreground">Save the product first to attach datasheets.</p>}
            {editing && !sheets.length && <p className="text-xs text-muted-foreground">No datasheets yet.</p>}
            <div className="space-y-1.5">
              {sheets.map((ds) => (
                <div key={ds.id} className="flex items-center justify-between rounded-md border border-border bg-slate-50/60 px-3 py-2" data-testid={`datasheet-${ds.id}`}>
                  <div className="flex min-w-0 items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-[#9D4848]" />
                    <span className="truncate text-sm text-slate-700">{ds.filename}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{fmtSize(ds.size)}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => downloadSheet(ds)} data-testid={`datasheet-dl-${ds.id}`}><Download className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => removeSheet(ds)} className="text-red-600 hover:bg-red-50" data-testid={`datasheet-del-${ds.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={save} disabled={busy} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="product-save">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{editing ? "Save Changes" : "Create Product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({ total: 0, categories: 0, with_datasheets: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [page, setPage] = useState(1);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s, c, sm] = await Promise.all([
        api.get("/catalog/products"), api.get("/suppliers"),
        api.get("/catalog/categories"), api.get("/catalog/summary"),
      ]);
      setProducts(p.data); setSuppliers(s.data); setCategories(c.data); setSummary(sm.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search, cat]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (cat !== "all" && p.category !== cat) return false;
      if (q && !`${p.sku} ${p.name} ${p.internal_description || ""} ${p.brand} ${p.mpn} ${p.category}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [products, search, cat]);
  const paged = useMemo(() => paginate(shown, page), [shown, page]);

  const onSaved = (data) => { setProducts((prev) => { const i = prev.findIndex((x) => x.id === data.id); if (i >= 0) { const n = [...prev]; n[i] = data; return n; } return [...prev, data]; }); api.get("/catalog/summary").then(({ data: sm }) => setSummary(sm)).catch(() => {}); api.get("/catalog/categories").then(({ data: c }) => setCategories(c)).catch(() => {}); };
  const confirmDelete = async () => { try { await api.delete(`/catalog/products/${delTarget.id}`); setProducts((p) => p.filter((x) => x.id !== delTarget.id)); toast.success("Product deleted"); } catch (e) { toast.error(apiError(e)); } finally { setDelTarget(null); } };

  const KPIS = [
    { label: "Products", value: summary.total, icon: Package, tint: "text-[#1B4383] bg-[#E6ECF5]" },
    { label: "Categories", value: summary.categories, icon: Tag, tint: "text-emerald-600 bg-emerald-50" },
    { label: "With Datasheets", value: summary.with_datasheets, icon: FileText, tint: "text-[#9D4848] bg-[#F4E7E7]" },
  ];

  return (
    <AppLayout title="Product Catalog" actions={<div className="flex gap-2"><Button size="sm" onClick={() => { setEditing(null); setDlgOpen(true); }} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="new-product-btn"><Plus className="mr-1.5 h-4 w-4" />New Product</Button></div>}>
      <div className="mb-4 grid grid-cols-3 gap-3" data-testid="catalog-kpis">
        {KPIS.map((k) => (
          <div key={k.label} className="rounded-lg border border-border bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-semibold tracking-wide text-slate-400">{k.label}</span>
              <span className={`flex h-7 w-7 items-center justify-center rounded-md ${k.tint}`}><k.icon className="h-4 w-4" /></span>
            </div>
            <div className="font-heading text-2xl font-bold text-slate-900">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-white p-4">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search SKU, name, brand or part no…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalog-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-[200px]" data-testid="catalog-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : !products.length ? (
        <div className="rounded-lg border border-dashed border-border bg-white py-16 text-center" data-testid="catalog-empty">
          <span className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#EAF1FB] text-[#1B4383]"><Boxes className="h-6 w-6" /></span>
          <div className="font-heading text-base font-bold text-slate-900">No products yet</div>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Add your first catalog product — then RFQ line items can resolve to it so every quote line maps to a real SKU.</p>
          <Button size="sm" onClick={() => { setEditing(null); setDlgOpen(true); }} className="mt-4 bg-[#1B4383] hover:bg-[#16386e]" data-testid="new-product-empty-btn"><Plus className="mr-1.5 h-4 w-4" />New Product</Button>
        </div>
      ) : !shown.length ? (
        <div className="rounded-lg border border-dashed border-border bg-white py-14 text-center text-sm text-muted-foreground" data-testid="catalog-no-match">No products match your filters.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-white" data-testid="catalog-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">UoM</th>
                <th className="px-4 py-3 font-medium">Brand / Part No.</th>
                <th className="px-4 py-3 font-medium">Default Supplier</th>
                <th className="px-4 py-3 text-center font-medium">Docs</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => (
                <tr key={p.id} className="cursor-pointer border-b border-border last:border-0 hover:bg-slate-50/60" onClick={() => { setEditing(p); setDlgOpen(true); }} data-testid={`product-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1B4383]">{p.sku}</td>
                  <td className="px-4 py-3"><div className="font-medium text-slate-800">{p.name}</div>{p.internal_description && <div className="mt-0.5 text-xs text-[#A87F31]" data-testid={`product-internal-${p.id}`}>{p.internal_description}</div>}</td>
                  <td className="px-4 py-3 text-slate-600">{p.category || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.uom || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{[p.brand, p.mpn].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.default_supplier_name || "—"}</td>
                  <td className="px-4 py-3 text-center">{p.datasheets?.length ? <span className="inline-flex items-center gap-1 rounded-full bg-[#F4E7E7] px-2 py-0.5 text-[11px] font-semibold text-[#9D4848]"><FileText className="h-3 w-3" />{p.datasheets.length}</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => setDelTarget(p)} className="text-red-600 hover:bg-red-50" data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && shown.length > 0 && <PaginationBar page={page} setPage={setPage} total={shown.length} testid="catalog-pagination" />}

      <ProductDialog open={dlgOpen} onOpenChange={setDlgOpen} initial={editing} suppliers={suppliers} categories={categories} onSaved={onSaved} />

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="product-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delTarget?.sku}?</AlertDialogTitle>
            <AlertDialogDescription>"{delTarget?.name}" will be removed from the catalog. Linked RFQ lines must be unlinked first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="product-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="mt-6"><ActivityLog url="/catalog/history?scope=catalog" refreshKey={products.length} /></div>
    </AppLayout>
  );
}
